<script setup>
import { computed } from 'vue'
import { Loader2, ZoomIn } from 'lucide-vue-next'

import { CommandGroup, CommandItem } from '@/components/ui/command'
import { NoResults } from '@/components/ui/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import {
    getCreatorCommandValue,
    SEARCH_MORE_CREATORS_COMMAND_VALUE,
    SHOW_MORE_RESULTS_COMMAND_VALUE,
} from './globalSearchCommandValues'
import GlobalSearchResultRow from './GlobalSearchResultRow.vue'

const props = defineProps({
    query: { type: String, required: true },
    visibleResults: { type: Array, required: true },
    pendingResults: { type: Array, required: true },
    isInitialLoading: { type: Boolean, required: true },
    isLookingForCreators: { type: Boolean, required: true },
    canSearchMoreCreators: { type: Boolean, required: true },
    noCreatorsFound: { type: Boolean, required: true },
})

const emit = defineEmits(['selectCreator', 'searchMoreCreators', 'showMoreResults'])

const hasVisibleResults = computed(() => props.visibleResults.length > 0)
const hasPendingResults = computed(() => props.pendingResults.length > 0)
const showLoadingPanel = computed(
    () => props.isInitialLoading || (props.isLookingForCreators && !hasVisibleResults.value && !hasPendingResults.value),
)
const showLoadingFooter = computed(() => props.isLookingForCreators && hasVisibleResults.value)

const searchMoreCreators = (event) => {
    event?.preventDefault?.()
    emit('searchMoreCreators')
}

const showMoreResults = (event) => {
    event?.preventDefault?.()
    emit('showMoreResults')
}
</script>

<template>
    <div class="border-t border-slate-100">
        <CommandGroup v-if="hasVisibleResults" class="p-1">
            <CommandItem
                v-for="creator in visibleResults"
                :key="creator.key"
                :value="getCreatorCommandValue(creator)"
                class="group rounded px-2 py-2 data-[highlighted]:bg-slate-100"
                @select="emit('selectCreator', creator)"
            >
                <GlobalSearchResultRow :creator="creator" :query="query" />
            </CommandItem>
        </CommandGroup>

        <div v-if="showLoadingPanel" class="px-4 py-3">
            <div class="mb-3 flex items-center gap-2 text-sm text-slate-600">
                <Loader2 class="h-4 w-4 animate-spin" />
                <span>Looking for creators...</span>
            </div>
            <div class="space-y-2">
                <div v-for="index in 4" :key="index" class="flex items-center gap-3">
                    <Skeleton class="h-9 w-9 rounded-full" />
                    <div class="min-w-0 flex-1 space-y-1.5">
                        <Skeleton class="h-3.5 w-2/5" />
                        <Skeleton class="h-3 w-1/4" />
                    </div>
                    <Skeleton class="h-3 w-10" />
                </div>
            </div>
        </div>

        <div v-if="showLoadingFooter" class="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-600">
            <Loader2 class="h-4 w-4 animate-spin" />
            <span>Looking for creators...</span>
        </div>

        <CommandGroup v-if="canSearchMoreCreators" class="border-t border-slate-100 p-1">
            <CommandItem
                :value="SEARCH_MORE_CREATORS_COMMAND_VALUE"
                class="justify-center rounded px-3 py-2 text-sm font-medium text-slate-700 data-[highlighted]:bg-slate-100 data-[highlighted]:text-slate-900"
                @select="searchMoreCreators"
            >
                <ZoomIn class="mr-2 h-4 w-4 text-slate-400" />
                Search more creators
            </CommandItem>
        </CommandGroup>

        <CommandGroup v-if="hasPendingResults" class="border-t border-slate-100 p-1">
            <CommandItem
                :value="SHOW_MORE_RESULTS_COMMAND_VALUE"
                class="justify-center rounded px-3 py-2 text-sm font-medium text-indigo-600 data-[highlighted]:bg-indigo-50 data-[highlighted]:text-indigo-700"
                @select="showMoreResults"
            >
                Show more results
            </CommandItem>
        </CommandGroup>

        <div v-if="noCreatorsFound" class="py-0">
            <NoResults
                variant="compact"
                title="No creators found"
                message="Try another name or platform."
                class="py-6"
            />
        </div>
    </div>
</template>
